/**
 * The shell icon set, as a typed sprite.
 *
 * Ported from the prototype rather than swapped for an icon library: Lucide's
 * metaphors do not cover rectifier, busway, RPP, DC bus, ATS or STS — and those
 * are precisely the icons that carry meaning here.
 *
 * stroke-width 2 at 15–17px is the prototype's actual weight and reads
 * correctly at this density. "Ultra-light lines" would lose legibility.
 */
import type { ReactNode } from 'react';

import { PATHS, type IconName } from './icon-paths';

export type { IconName } from './icon-paths';

export interface IconProps {
  name: IconName;
  size?: number;
  className?: string;
}

export function Icon({ name, size = 16, className }: IconProps): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      /*
       * 1.6, DOWN FROM 2, and it is the design language's weight rather than a preference.
       *
       * Its ~130 glyphs are drawn at `stroke-width: 1.6` on the same 24-unit grid, so a 2.0
       * stroke here would have put every icon ported from it a quarter heavier than the ones
       * beside it — the kind of mismatch that reads as "this screen is unfinished" without
       * anybody being able to say why.
       *
       * The trade is real and worth naming: the design draws at 20px and this component's
       * default is 16, so a glyph here is optically lighter than the same glyph there. It is
       * still `currentColor`, so it inherits an ink already measured against its ground — the
       * stroke got thinner, not fainter, and WCAG 1.4.11 is about contrast rather than weight.
       * If a 12px use ever reads as too faint, the fix is to draw it larger, not to thicken
       * one caller out of step with the set.
       */
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={{ flex: 'none' }}
      aria-hidden
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
